import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, money, d } from "@/api";
import { MODE_LABELS } from "@/components/Badge";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  supplier_id: "", kind: "spot", mode: "kara", origin: "", destination: "",
  unit: "sevkiyat", price: "", currency: "USD", valid_from: "", valid_until: "",
};

export default function Tariffs() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/tariffs").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    load();
    api.get("/parties", { params: { type: "tedarikci" } }).then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tariffs", {
        ...form, price: Number(form.price),
        valid_from: form.valid_from || null, valid_until: form.valid_until || null,
      });
      toast.success("Tarife eklendi");
      setForm(EMPTY); setOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "-";

  const remove = async (r) => {
    if (!window.confirm(`${supName(r.supplier_id)} tarifesi (${r.origin} → ${r.destination}) kalıcı olarak silinecek. Devam edilsin mi?`)) return;
    try {
      await api.delete(`/tariffs/${r.id}`);
      toast.success("Tarife silindi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6" data-testid="tariffs-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline">Fiyatlama</p>
          <h1 className="font-display text-4xl font-black tracking-tighter">Tarifeler</h1>
        </div>
        {can("quote:write") && (
          <button className="brut-btn" onClick={() => setOpen(!open)} data-testid="new-tariff-button">
            <Plus className="h-4 w-4" /> Yeni Tarife
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={save} className="brut grid animate-fade-up grid-cols-1 gap-3 p-4 sm:grid-cols-4" data-testid="tariff-form">
          <select required className="brut-input" value={form.supplier_id}
            onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} data-testid="tariff-supplier-select">
            <option value="">Tedarikçi</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select className="brut-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="tariff-kind-select">
            <option value="spot">Spot</option><option value="sozlesmeli">Sözleşmeli</option>
          </select>
          <select className="brut-input" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="tariff-mode-select">
            <option value="kara">Kara</option><option value="deniz">Deniz</option><option value="hava">Hava</option>
          </select>
          <select className="brut-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="tariff-unit-select">
            <option value="sevkiyat">Sevkiyat</option><option value="kg">kg</option><option value="m3">m³</option>
          </select>
          <input required placeholder="Çıkış" className="brut-input" value={form.origin}
            onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="tariff-origin-input" />
          <input required placeholder="Varış" className="brut-input" value={form.destination}
            onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="tariff-destination-input" />
          <input required type="number" step="0.01" placeholder="Fiyat" className="brut-input" value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="tariff-price-input" />
          <select className="brut-input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="tariff-currency-select">
            {["TRY", "USD", "EUR", "GBP"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input type="date" className="brut-input" value={form.valid_from}
            onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="tariff-validfrom-input" />
          <input type="date" className="brut-input" value={form.valid_until}
            onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="tariff-validuntil-input" />
          <button className="brut-btn sm:col-span-2" data-testid="tariff-save-button">Tarifeyi Kaydet</button>
        </form>
      )}

      <div className="brut overflow-x-auto" data-testid="tariffs-table">
        <table className="dense w-full">
          <thead><tr><th>Tedarikçi</th><th>Tip</th><th>Mod</th><th>Rota</th><th>Birim</th><th className="!text-right">Fiyat</th><th>Geçerlilik</th><th className="!text-right">İşlem</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`tariff-row-${r.id}`}>
                <td className="font-semibold">{supName(r.supplier_id)}</td>
                <td>{r.kind === "spot" ? "Spot" : "Sözleşmeli"}</td>
                <td>{MODE_LABELS[r.mode]}</td>
                <td className="text-xs">{r.origin} → {r.destination}</td>
                <td>{r.unit}</td>
                <td className="tnum text-right font-mono">{money(r.price, r.currency)}</td>
                <td className="text-xs">{d(r.valid_from)} - {d(r.valid_until)}</td>
                <td className="text-right">
                  {can("*") && (
                    <button onClick={() => remove(r)} className="text-zinc-400 hover:text-red-600"
                      data-testid={`delete-tariff-${r.id}`} title="Kalıcı olarak sil">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-zinc-500">Tanımlı tarife yok.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
